import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import BackspaceIcon from '@mui/icons-material/Backspace';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

import { press, prettyExpr, groupDigits, preview, resultText } from './calcEngine';
import { copyText } from '../clipboard';
import { useTools } from '../../contextApi/toolsContext';
import { useToolTokens, keepFocus, useFocusOnOpen } from './toolStyles';

// The floating calculator, laid out and keyed like the Windows Calculator (Standard): % CE C ⌫ / 1/x x² √ ÷ / 7 8 9 × / 4 5 6 − /
// 1 2 3 + / ± 0 . =, with the memory row (MC MR M+ M− MS) above. The maths and every key rule are in calcEngine.js (pure,
// unit-tested). THIS FILE HAS NO KEYBOARD HANDLER: the keys are routed by FloatingTools (floatingTools.js), which hands them to the
// calculator while it owns the keyboard - that is what lets it work over an open form without taking the form's focus.
// The sum, the memory, the history and whether the history list is showing live in ToolsProvider, so closing the window (F6)
// and opening it again - or moving to another section, or reloading the tab - finds them exactly as they were.

const ERROR_KEYS = { div0: 'tools.errDiv0', syntax: 'tools.errSyntax', overflow: 'tools.errOverflow', invalid: 'tools.errInvalid' };

const KEYS = [
  { k: '%', kind: 'fn' }, { k: 'ce', label: 'CE', kind: 'fn', tip: 'tools.keyCe' }, { k: 'clear', label: 'C', kind: 'fn', tip: 'tools.keyC' }, { k: 'back', kind: 'fn', tip: 'tools.keyBack' },
  { k: 'recip', label: '1/x', kind: 'fn', tip: 'tools.keyRecip' }, { k: 'sqr', label: 'x²', kind: 'fn', tip: 'tools.keySquare' }, { k: 'sqrt', label: '√', kind: 'fn', tip: 'tools.keySqrt' }, { k: '/', label: '÷', kind: 'op' },
  { k: '7' }, { k: '8' }, { k: '9' }, { k: '*', label: '×', kind: 'op' },
  { k: '4' }, { k: '5' }, { k: '6' }, { k: '-', label: '−', kind: 'op' },
  { k: '1' }, { k: '2' }, { k: '3' }, { k: '+', kind: 'op' },
  { k: 'neg', label: '±', tip: 'tools.keyNeg' }, { k: '0' }, { k: '.' }, { k: '=', kind: 'eq' },
];

const MEMORY = [
  { k: 'mc', label: 'MC', tip: 'tools.memClear' }, { k: 'mr', label: 'MR', tip: 'tools.memRecall' }, { k: 'mplus', label: 'M+', tip: 'tools.memAdd' },
  { k: 'mminus', label: 'M−', tip: 'tools.memSub' }, { k: 'ms', label: 'MS', tip: 'tools.memStore' },
];

const clamp = (lo, v, hi) => Math.max(lo, Math.min(hi, v));

export default function CalculatorTool({ size, mobile }) {
  const T = useToolTokens();
  const { t } = useTranslation();
  const { calc, setCalc, calcPanel, setCalcPanel } = useTools();
  const rootRef = useRef(null);
  useFocusOnOpen(rootRef);

  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);
  const flashCopied = () => {
    setCopied(true);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 1500);
  };
  // Ctrl+C is handled by FloatingTools (the calculator may not have the DOM focus); it tells us so the button can say "Copied".
  useEffect(() => {
    window.addEventListener('xms-tools-copied', flashCopied);
    return () => window.removeEventListener('xms-tools-copied', flashCopied);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const pressKey = (k) => setCalc((s) => press(s, k));
  const hasMemory = calc.memory !== null && calc.memory !== undefined;

  // ── what the display says ──
  const bare = /^-?[0-9.]*$/.test(calc.expr);                        // just a number so far: nothing to show on the top line
  const live = !calc.evaluated && !calc.error && !bare ? preview(calc.expr) : null;
  let big;
  if (calc.error) big = t(ERROR_KEYS[calc.error] || ERROR_KEYS.syntax);
  else if (calc.evaluated) big = groupDigits(calc.expr);
  else if (bare) big = calc.expr ? groupDigits(calc.expr) : '0';
  else if (live !== null) big = groupDigits(live);
  else big = groupDigits((calc.expr.match(/[0-9.]+(?=[^0-9.]*$)/) || ['0'])[0]);   // "5+" shows the 5
  const topText = calc.evaluated ? (calc.prev ? `${prettyExpr(calc.prev)} =` : '') : bare ? '' : prettyExpr(calc.expr);
  const copyValue = resultText(calc);

  const dispH = mobile ? clamp(120, size.h * 0.2, 180) : clamp(88, size.h * 0.18, 124);
  const memH = mobile ? 40 : 26;
  const topPx = mobile ? 15 : 12.5;
  const maxTop = Math.max(8, Math.floor((size.w - 60) / (topPx * 0.58)));
  const shownTop = topText.length > maxTop ? `…${topText.slice(topText.length - maxTop + 1)}` : topText;
  const bigBase = clamp(24, size.h * 0.08, mobile ? 52 : 40);
  const bigPx = calc.error ? Math.min(bigBase, 18) : Math.max(15, Math.min(bigBase, (size.w - 28) / (Math.max(big.length, 1) * 0.62)));

  const gap = mobile ? 8 : 6;
  const pad = mobile ? 12 : 10;
  const keyH = (size.h - dispH - memH - 2 * pad - 5 * gap - gap) / 6;
  const keyPx = clamp(14, keyH * 0.38, mobile ? 30 : 24);

  const doCopy = async () => {
    if (!copyValue) return;
    if (await copyText(copyValue)) flashCopied();
  };
  const takeEntry = (entry) => {
    setCalc((s) => ({ ...s, expr: entry.result, prev: entry.expr, evaluated: true, error: '' }));
    setCalcPanel(false);
  };

  const keyBg = { digit: T.CTRL, fn: T.CTRL_HOVER, op: T.BD2, eq: T.ACCENT };
  const iconBtn = (label, icon, onClick, disabled, active) => (
    <Tooltip title={label}>
      <span>
        <IconButton size="small" aria-label={label} disabled={disabled} onMouseDown={keepFocus} onClick={onClick}
          sx={{ width: mobile ? 36 : 26, height: mobile ? 36 : 26, color: active ? T.TEXT : T.TEXT2, '&:hover': { color: T.TEXT, bgcolor: T.CTRL_HOVER } }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box ref={rootRef} tabIndex={-1} data-tool="calculator"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', outline: 'none' }}>

      {/* display */}
      <Box data-display sx={{
        height: dispH, flexShrink: 0, px: 1.75, pt: 1, pb: 0.75, position: 'relative',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderBottom: `1px solid ${T.BD}`,
      }}>
        <Box sx={{ position: 'absolute', top: 6, insetInlineStart: 8, display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {iconBtn(t('tools.history'), <HistoryIcon sx={{ fontSize: mobile ? 20 : 16 }} />, () => setCalcPanel(!calcPanel), false, calcPanel)}
          {iconBtn(copied ? t('tools.copied') : t('tools.copy'),
            copied ? <CheckIcon sx={{ fontSize: mobile ? 20 : 16 }} /> : <ContentCopyIcon sx={{ fontSize: mobile ? 19 : 15 }} />, doCopy, !copyValue)}
          {hasMemory && (
            <Typography data-memory-indicator dir="ltr" title={t('tools.memory')}
              sx={{ ml: 0.75, px: 0.75, borderRadius: '5px', bgcolor: T.CTRL_HOVER, color: T.TEXT, fontSize: mobile ? '0.78rem' : '0.66rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              M {groupDigits(String(calc.memory))}
            </Typography>
          )}
        </Box>
        <Typography data-calc-expr dir="ltr" noWrap
          sx={{ textAlign: 'right', fontSize: topPx, color: T.TEXT2, minHeight: topPx * 1.4, fontVariantNumeric: 'tabular-nums' }}>
          {shownTop}
        </Typography>
        <Typography data-calc-result dir="ltr" aria-live="polite"
          sx={{ textAlign: 'right', fontSize: bigPx, fontWeight: 700, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums',
            color: calc.error ? 'error.main' : T.TEXT, whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {big}
        </Typography>
      </Box>

      {/* memory */}
      <Box data-memory-row sx={{ height: memH, flexShrink: 0, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', px: `${pad}px`, pt: `${gap}px`, boxSizing: 'content-box' }}>
        {MEMORY.map((m) => {
          const off = (m.k === 'mc' || m.k === 'mr') && !hasMemory;
          return (
            <Box key={m.k} component="button" type="button" data-key={m.k} disabled={off} title={t(m.tip)}
              onMouseDown={keepFocus} onClick={() => pressKey(m.k)}
              sx={{
                border: 0, background: 'none', cursor: off ? 'default' : 'pointer', font: 'inherit', touchAction: 'manipulation', userSelect: 'none',
                borderRadius: '6px', fontSize: mobile ? '0.85rem' : '0.7rem', fontWeight: 700, color: off ? T.TEXT3 : T.TEXT,
                '&:hover': off ? {} : { bgcolor: T.CTRL_HOVER },
              }}>
              {m.label}
            </Box>
          );
        })}
      </Box>

      {calcPanel ? (
        <Box data-history sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
          {calc.history.length === 0 && (
            <Typography sx={{ p: 2, textAlign: 'center', fontSize: '0.78rem', color: T.TEXT3 }}>{t('tools.noHistory')}</Typography>
          )}
          {calc.history.map((h, i) => (
            <Box key={`${i}-${h.expr}`} component="button" type="button" data-history-item onMouseDown={keepFocus} onClick={() => takeEntry(h)}
              sx={{
                display: 'block', width: '100%', textAlign: 'right', border: 0, background: 'none', cursor: 'pointer', font: 'inherit',
                px: 1.25, py: 0.9, borderRadius: '8px', color: T.TEXT, direction: 'ltr',
                '&:hover': { bgcolor: T.CTRL_HOVER },
              }}>
              <Box component="span" sx={{ display: 'block', fontSize: '0.74rem', color: T.TEXT2 }}>{prettyExpr(h.expr)} =</Box>
              <Box component="span" sx={{ display: 'block', fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{groupDigits(h.result)}</Box>
            </Box>
          ))}
          {calc.history.length > 0 && (
            <Button size="small" fullWidth onMouseDown={keepFocus} onClick={() => setCalc((s) => ({ ...s, history: [] }))}
              sx={{ mt: 1, textTransform: 'none', color: T.TEXT2, fontSize: '0.74rem' }}>
              {t('tools.clearHistory')}
            </Button>
          )}
        </Box>
      ) : (
        <Box data-keypad sx={{
          flexGrow: 1, minHeight: 0, p: `${pad}px`, pt: `${gap}px`, display: 'grid', gap: `${gap}px`,
          gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: 'repeat(6, 1fr)',
        }}>
          {KEYS.map((key) => {
            const kind = key.kind || 'digit';
            return (
              <Box key={key.k} component="button" type="button" data-key={key.k} title={key.tip ? t(key.tip) : undefined}
                aria-label={key.tip ? t(key.tip) : undefined}
                onMouseDown={keepFocus} onClick={() => pressKey(key.k)}
                sx={{
                  border: 0, cursor: 'pointer', font: 'inherit', touchAction: 'manipulation', userSelect: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0,
                  borderRadius: '10px', fontSize: keyPx, fontWeight: kind === 'digit' ? 500 : 700,
                  color: kind === 'eq' ? T.ON_ACCENT : T.TEXT, bgcolor: keyBg[kind],
                  '&:hover': { filter: 'brightness(1.12)' }, '&:active': { transform: 'scale(0.97)' },
                }}>
                {key.k === 'back' ? <BackspaceIcon sx={{ fontSize: keyPx * 0.9 }} /> : (key.label || key.k)}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
